import { ChangeEvent, useEffect, useState } from "react";
import { useGlobalNotifications } from "../../../../hooks/use-global-notifications";
import { canRequestPush, isPushActive, subscribeToPush, unsubscribePush } from "../../../../utils/push-utils";
import { isNative } from "../../../../platform";
import { registerPush, getPushPermissionStatus, getCachedNativeToken } from "../../../../platform/push";
import { useAuth } from "../../../../contexts/auth/auth-context";
import { notificationsApi } from "../../../../api/notifications/notifications";

type NotificationMethod = "sms" | "email" | "both";

type UseNotificationsSettingsArgs = {
  initialNotificationMethod?: string | null | undefined;
  initialSmsConsent?: boolean | undefined;
  onSettingsChanged?: ((method: string | null, consent: boolean) => void) | undefined;
};

export const useNotificationsSettings = ({
  initialNotificationMethod = null,
  initialSmsConsent = false,
  onSettingsChanged,
}: UseNotificationsSettingsArgs = {}) => {
  const { showError } = useGlobalNotifications();
  const { updateProfile } = useAuth();
  const [email, setEmail] = useState(false);
  const [sms, setSms] = useState(false);
  const [smsConsented, setSmsConsented] = useState(initialSmsConsent);

  // Load current notification preferences from initial data (prefetched)
  useEffect(() => {
    const notificationMethod = initialNotificationMethod as NotificationMethod;
    if (notificationMethod === "email") {
      setEmail(true);
      setSms(false);
    } else if (notificationMethod === "sms") {
      setEmail(false);
      setSms(true);
    } else if (notificationMethod === "both") {
      setEmail(true);
      setSms(true);
    }
    setSmsConsented(initialSmsConsent);
  }, [initialNotificationMethod, initialSmsConsent]);

  // Convert checkbox states to notification_method enum
  const getNotificationMethod = (): NotificationMethod | null => {
    if (email && sms) return "both";
    if (email) return "email";
    if (sms) return "sms";
    return null;
  };

  // Save notification preferences to backend
  const saveSettings = async () => {
    try {
      const notificationMethod = getNotificationMethod();
      const updateData: {
        notification_method: NotificationMethod | null;
        sms_consent?: boolean;
      } = {
        notification_method: notificationMethod,
      };

      if (notificationMethod === "sms" || notificationMethod === "both") {
        updateData.sms_consent = true;
      }

      await updateProfile(updateData as any);

      // Notify parent so notificationsData stays in sync across tab switches
      onSettingsChanged?.(notificationMethod, updateData.sms_consent ?? smsConsented);
    } catch (error) {
      console.error("Failed to save notification settings:", error);
      showError("Failed to save notification settings");
      throw error;
    }
  };

  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const pushSupported = isNative() ? true : canRequestPush();
  const pushDenied = isNative()
    ? false  // native permission is handled by NativePermissionsGate
    : typeof Notification !== 'undefined' && Notification.permission === 'denied';

  // Initialize push state from the active subscription
  useEffect(() => {
    if (isNative()) {
      // On native, check if permission is granted (FCM registration handled by gate)
      getPushPermissionStatus().then((s) => setPushEnabled(s === 'granted'));
    } else {
      isPushActive().then(setPushEnabled);
    }
  }, []);

  const togglePush = async (enable: boolean) => {
    console.log('[NotifSettings] togglePush called, enable:', enable, 'isNative:', isNative());
    setPushLoading(true);
    try {
      if (enable) {
        if (isNative()) {
          console.log('[NotifSettings] calling registerPush() for native…');
          const ok = await registerPush();
          console.log('[NotifSettings] registerPush() returned:', ok);
          setPushEnabled(ok);
        } else {
          const ok = await subscribeToPush();
          setPushEnabled(ok);
        }
      } else {
        if (isNative()) {
          const token = getCachedNativeToken();
          if (token) {
            await notificationsApi.deletePushToken(token);
          }
        } else {
          await unsubscribePush();
        }
        setPushEnabled(false);
      }
    } catch (err) {
      console.error('[NotifSettings] togglePush error:', err);
    } finally {
      setPushLoading(false);
    }
  };

  const handlePushCardClick = async () => {
    if (pushLoading || !pushSupported) return;
    await togglePush(!pushEnabled);
  };

  const handlePushChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (pushLoading || !pushSupported) return;
    await togglePush(event.target.checked);
  };

  const showConsent = sms && !smsConsented;

  // Save immediately after toggling
  const saveAfterToggle = (nextEmail: boolean, nextSms: boolean, nextConsent: boolean) => {
    const method = nextEmail && nextSms ? 'both' : nextEmail ? 'email' : nextSms ? 'sms' : null;
    const consent = (method === 'sms' || method === 'both') ? true : nextConsent;

    // Update parent state immediately (so re-opening settings shows correct state)
    onSettingsChanged?.(method, consent);

    // Persist to backend + update auth context profile (fire-and-forget)
    updateProfile({
      notification_method: method,
      ...(consent ? { sms_consent: true } : {}),
    } as Partial<import("../../../../api/profile/profile.types").Profile>).catch((err: unknown) => {
      console.error('Failed to save notification settings:', err);
      showError('Failed to save notification settings');
    });
  };

  const handleEmailCardClick = () => {
    const next = !email;
    setEmail(next);
    saveAfterToggle(next, sms, smsConsented);
  };

  const handleSmsCardClick = () => {
    const next = !sms;
    setSms(next);
    if (next && !smsConsented) return; // wait for consent confirmation
    saveAfterToggle(email, next, smsConsented);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.checked);
    saveAfterToggle(event.target.checked, sms, smsConsented);
  };

  const handleSmsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSms(event.target.checked);
    if (event.target.checked && !smsConsented) return; // wait for consent
    saveAfterToggle(email, event.target.checked, smsConsented);
  };

  const handleCancelSmsConsent = () => {
    setSms(false);
    setSmsConsented(false);
    saveAfterToggle(email, false, false);
  };

  const handleConfirmSmsConsent = async () => {
    setSmsConsented(true);
    setSms(true);
    // Save the settings after confirming SMS consent
    try {
      await saveSettings();
    } catch (error) {
      console.error("Failed to save SMS consent:", error);
    }
  };


  return {
    email,
    sms,
    smsConsented,
    showConsent,
    handleEmailCardClick,
    handleSmsCardClick,
    handleEmailChange,
    handleSmsChange,
    handleCancelSmsConsent,
    handleConfirmSmsConsent,
    pushEnabled,
    pushLoading,
    pushSupported,
    pushDenied,
    handlePushCardClick,
    handlePushChange,
  };
};
